import { Autocomplete, Box, TextField, IconButton } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import Colors from "Constants/Color";
import { api } from "helpers/api_url";
import { useEffect, useState, useCallback } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import SubmitButton from "components/Buttons/SubmitButton";
import { showConfirm } from "utils/swalHelper";

interface BagOption {
    inventory_item_id: string | number;
    bag_no: string;
    available_quantity: number;
}

interface BagRow {
    id: string;
    inventory_item_id: string | number;
    bag_no: string;
    available_quantity: number;
    issue_quantity: number;
}

interface BagWithoutId {
    inventory_item_id: string | number;
    bag_no: string;
    available_quantity: number;
    issue_quantity: number;
}

interface BagSelectionModalProps {
    show: boolean;
    tableFormik: any;
    handleClose: () => void;
    quantity: number;
    onSave: (bags: BagWithoutId[]) => void;
    initialBags?: BagWithoutId[];
    bagQuantityValidation?: Array<{
        inventory_item_id: string | number;
        available_quantity: number;
        issue_quantity: number;
    }>;
    setBagQuantityValidation?: (bags: Array<{
        inventory_item_id: string | number;
        available_quantity: number;
        issue_quantity: number;
    }>) => void;
    editingItemId?: number | null;
    currentItemBags?: BagWithoutId[];
    editingRowData?: any;
    warehouseId?: string | number;
}

const BagSelectionModal: React.FC<BagSelectionModalProps> = ({
    show,
    tableFormik,
    handleClose,
    quantity,
    onSave,
    initialBags = [],
    bagQuantityValidation = [],
    setBagQuantityValidation,
    editingItemId = null,
    currentItemBags = [],
    editingRowData = null,
    warehouseId
}) => {
    const [bagOptions, setBagOptions] = useState<BagOption[]>([]);
    const [rows, setRows] = useState<BagRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingBagId, setEditingBagId] = useState<string | null>(null);

    const fetchBagDropdown = useCallback(async () => {
        try {
            setLoading(true);
            // Use editing row data if editing, otherwise use tableFormik values
            const itemName = editingItemId !== null && editingRowData
                ? editingRowData?.item?.item_name
                : tableFormik?.values?.item?.item_name;
            const categoryName = editingItemId !== null && editingRowData
                ? editingRowData?.category?.category_name
                : tableFormik?.values?.category?.category_name;
            const subCategoryName = editingItemId !== null && editingRowData
                ? editingRowData?.sub_category?.sub_category_name
                : tableFormik?.values?.sub_category?.sub_category_name;

            // Build query parameters
            const params = new URLSearchParams();
            if (itemName) params.append('item_name', itemName);
            if (categoryName) params.append('category_name', categoryName);
            if (subCategoryName) params.append('sub_category_name', subCategoryName);
            if (warehouseId) params.append('warehouse_id', String(warehouseId));

            const result = await Axios.get(`${api?.warehouse?.item_inward?.bag_dropdown}?${params.toString()}`);
            setBagOptions(result?.data?.data || []);
        } catch (err) {
            toast.error("Failed to fetch bag dropdown");
        } finally {
            setLoading(false);
        }
    }, [editingItemId, editingRowData, tableFormik, warehouseId]);

    useEffect(() => {
        if (show) {
            fetchBagDropdown();
            // Ensure all bags have an id field for modal internal use
            const bagsWithId: BagRow[] = (initialBags || []).map((bag, idx) => ({
                ...bag,
                id: `bag-${Date.now()}-${idx}`,
            }));
            setRows(bagsWithId);
            setEditingBagId(null);
            // Reset formik form when modal opens to clear any previous values
            formik.resetForm();
        }
    }, [show, initialBags, fetchBagDropdown]);

    const validationSchema = Yup.object({
        bag_no: Yup.object().nullable().required("Bag No. is required"),
        available_quantity: Yup.number()
            .typeError("Available Quantity must be a number")
            .positive("Available Quantity must be greater than 0")
            .required("Available Quantity is required"),
        issue_quantity: Yup.number()
            .typeError("Issue Quantity must be a number")
            .positive("Issue Quantity must be greater than 0")
            .required("Issue Quantity is required")
            .test(
                "less-than-available",
                "Issue Quantity must be less than or equal to Available Quantity",
                function (value) {
                    const availableQty = parseFloat(this.parent.available_quantity) || 0;
                    const issueQty = parseFloat(String(value)) || 0;
                    return issueQty <= availableQty;
                }
            ),
    });

    interface FormValues {
        bag_no: BagOption | null;
        available_quantity: string | number;
        issue_quantity: string | number;
    }

    const formik = useFormik<FormValues>({
        initialValues: {
            bag_no: null,
            available_quantity: "",
            issue_quantity: "",
        },
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            const issueQty = parseFloat(String(values.issue_quantity)) || 0;
            const availableQty = parseFloat(String(values.available_quantity)) || 0;

            // Check for duplicate bags, excluding the row being edited
            const rowsExcludingEdited = rows.filter(row => row.id !== editingBagId);
            if (rowsExcludingEdited?.some((r: any) => r?.bag_no === values?.bag_no?.bag_no)) {
                toast.error("Bag already added");
                return;
            }
            if (issueQty > availableQty) {
                formik.setFieldError("issue_quantity", `Issue Quantity (${issueQty}) cannot exceed Available Quantity (${availableQty})`);
                toast.error(`Issue Quantity (${issueQty}) cannot exceed Available Quantity (${availableQty})`);
                return;
            }

            if (!values.bag_no) {
                formik.setFieldError("bag_no", "Bag No. is required");
                return;
            }

            // Extract bag_no after null check so TypeScript knows it's not null
            const selectedBag = values.bag_no;

            // Check total issue quantity (excluding the row being edited)
            const currentTotal = rowsExcludingEdited.reduce((sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0), 0);
            const newTotal = currentTotal + issueQty;

            if (newTotal > quantity) {
                formik.setFieldError("issue_quantity", `Total Issue Quantity (${newTotal.toFixed(2)}) cannot exceed ${quantity}`);
                toast.error(`Total Issue Quantity (${newTotal.toFixed(2)}) cannot exceed ${quantity}`);
                return;
            }

            if (editingBagId !== null) {
                // Update existing row
                const updatedRows = rows.map((row) =>
                    row.id === editingBagId
                        ? {
                            id: editingBagId,
                            inventory_item_id: selectedBag.inventory_item_id,
                            bag_no: selectedBag.bag_no,
                            available_quantity: availableQty,
                            issue_quantity: issueQty,
                        }
                        : row
                );
                setRows(updatedRows);
                setEditingBagId(null);
            } else {
                // Add new row
                const newRow: BagRow = {
                    id: Date.now().toString(),
                    inventory_item_id: selectedBag.inventory_item_id,
                    bag_no: selectedBag.bag_no,
                    available_quantity: availableQty,
                    issue_quantity: issueQty,
                };
                setRows([...rows, newRow]);
            }

            resetForm();
        },
    });

    const handleDelete = async (id: string) => {
        const rowToDelete = rows.find((row) => row.id === id);
        const bagNo = rowToDelete?.bag_no || "this bag";
        
        const result = await showConfirm(
            `Are you sure you want to remove ${bagNo}? Deleting it may prevent adding it again in this order.`,
            "Confirm Deletion",
            "Delete",
            "Cancel"
        );
        
        if (result?.isConfirmed) {
            setRows(rows.filter((row) => row.id !== id));
            if (editingBagId === id) {
                setEditingBagId(null);
                formik.resetForm();
            }
        }
    };

    const handleEdit = (row: BagRow) => {
        setEditingBagId(row.id);
        // Find the latest bag option from bagOptions that matches this row
        const bagOption = bagOptions.find(
            (option) => option.bag_no === row.bag_no && option.inventory_item_id === row.inventory_item_id
        );

        // Use latest available quantity from bagOptions if found, otherwise fallback to row's available_quantity
        const latestAvailableQuantity = bagOption
            ? Number(bagOption.available_quantity) + Number(row?.issue_quantity)
            : Number(row.available_quantity) + Number(row?.issue_quantity);

        // Create bag option object for formik (use latest data)
        const bagOptionForForm = bagOption || {
            inventory_item_id: row.inventory_item_id,
            bag_no: row.bag_no,
            available_quantity: latestAvailableQuantity,
        };

        formik.setFieldValue("bag_no", bagOptionForForm);
        formik.setFieldValue("available_quantity", latestAvailableQuantity);
        formik.setFieldValue("issue_quantity", row.issue_quantity);
    };

    const handleCancelEdit = () => {
        setEditingBagId(null);
        formik.resetForm();
    };

    const handleSave = () => {
        const totalIssueQuantity = rows.reduce((sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0), 0);
        const quantityValue = parseFloat(String(quantity)) || 0;

        if (rows.length === 0) {
            toast.error("Please add at least one bag");
            return;
        }

        // Check if total issue quantity exceeds the quantity entered in table
        if (totalIssueQuantity > quantityValue) {
            toast.error(`Total Issue Quantity (${totalIssueQuantity.toFixed(2)}) cannot exceed Quantity (${quantityValue.toFixed(2)})`);
            return;
        }

        // Check if total issue quantity equals the quantity entered in table
        const tolerance = 0.01; // Small tolerance for floating point comparison
        if (Math.abs(totalIssueQuantity - quantityValue) > tolerance) {
            toast.error(`Total Issue Quantity (${totalIssueQuantity.toFixed(2)}) must be equal to Quantity (${quantityValue.toFixed(2)}) entered in table`);
            return;
        }

        // Update bagQuantityValidation state
        if (setBagQuantityValidation) {
            // First, remove old bags from initialBags (bags that were previously saved)
            // This handles both editing existing items and reopening modal for already saved items
            let updatedBagValidation = [...bagQuantityValidation];

            // Remove old bags from validation state using initialBags
            // initialBags contains the bags that were previously saved for this item
            if (initialBags && initialBags.length > 0) {
                initialBags.forEach((oldBag: any) => {
                    const bagIndex = updatedBagValidation.findIndex(
                        (b) => String(b.inventory_item_id) === String(oldBag.inventory_item_id)
                    );
                    if (bagIndex !== -1) {
                        updatedBagValidation[bagIndex].issue_quantity -= parseFloat(String(oldBag.issue_quantity)) || 0;
                        // Remove if issue_quantity becomes 0 or negative
                        if (updatedBagValidation[bagIndex].issue_quantity <= 0) {
                            updatedBagValidation.splice(bagIndex, 1);
                        }
                    }
                });
            }

            // Validate all bags before updating state
            let validationError = false;
            for (const row of rows) {
                const inventoryItemId = row.inventory_item_id;
                const issueQty = parseFloat(String(row.issue_quantity)) || 0;
                const availableQty = parseFloat(String(row.available_quantity)) || 0;

                // Find if bag already exists in validation state
                const existingBagIndex = updatedBagValidation.findIndex(
                    (b) => String(b.inventory_item_id) === String(inventoryItemId)
                );

                if (existingBagIndex !== -1) {
                    // Bag exists, append issue quantity
                    const newTotalIssueQty = updatedBagValidation[existingBagIndex].issue_quantity + issueQty;

                    // Validate that total issue quantity doesn't exceed available quantity
                    if (newTotalIssueQty > availableQty) {
                        toast.error(`Bag is used for another item also and issued quantity (${newTotalIssueQty.toFixed(2)}) is going greater than available quantity (${availableQty.toFixed(2)})`);
                        validationError = true;
                        break;
                    }
                } else {
                    // Bag doesn't exist, validate new entry
                    if (issueQty > availableQty) {
                        toast.error(`Issued quantity (${issueQty.toFixed(2)}) cannot exceed available quantity (${availableQty.toFixed(2)})`);
                        validationError = true;
                        break;
                    }
                }
            }

            // If validation failed, don't update state
            if (validationError) {
                return;
            }

            // All validations passed, update the state
            for (const row of rows) {
                const inventoryItemId = row.inventory_item_id;
                const issueQty = parseFloat(String(row.issue_quantity)) || 0;
                const availableQty = parseFloat(String(row.available_quantity)) || 0;

                const existingBagIndex = updatedBagValidation.findIndex(
                    (b) => String(b.inventory_item_id) === String(inventoryItemId)
                );

                if (existingBagIndex !== -1) {
                    // Bag exists, append issue quantity
                    updatedBagValidation[existingBagIndex].issue_quantity += issueQty;
                } else {
                    // Bag doesn't exist, add new entry
                    updatedBagValidation.push({
                        inventory_item_id: inventoryItemId,
                        available_quantity: availableQty,
                        issue_quantity: issueQty,
                    });
                }
            }

            setBagQuantityValidation(updatedBagValidation);
        }

        // Remove id field from bags before saving (id is only for internal modal use)
        const bagsToSave = rows.map(({ id, ...bag }) => bag);
        onSave(bagsToSave);
        handleClose();
    };

    const handleModalClose = () => {
        // Reset formik form when modal is closed
        formik.resetForm();
        setEditingBagId(null);
        // Reset rows to initial bags to discard any unsaved changes
        const bagsWithId: BagRow[] = (initialBags || []).map((bag, idx) => ({
            ...bag,
            id: `bag-${Date.now()}-${idx}`,
        }));
        setRows(bagsWithId);
        handleClose();
    };

    const totalIssueQuantity = rows.reduce((sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0), 0);
    const remainingQuantity = quantity - totalIssueQuantity;

    return (
        <Modal show={show} onHide={handleModalClose} centered size="xl" backdrop="static">
            <Modal.Header className="border-0">
                <Modal.Title className="fw-bold">Select Bags</Modal.Title>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleModalClose}
                />
            </Modal.Header>
            <hr />
            <Modal.Body>
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                            Entered Quantity:
                        </span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                            {quantity}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                            Total Issue Quantity:
                        </span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                            {totalIssueQuantity.toFixed(2)}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                            Remaining Quantity:
                        </span>
                        <span
                            style={{
                                fontSize: "0.875rem",
                                fontWeight: "500",
                                color: remainingQuantity >= 0 ? "#28a745" : "#dc3545",
                            }}
                        >
                            {remainingQuantity.toFixed(2)}
                        </span>
                    </div>
                </div>

                <style>
                    {`
                        .sticky-column {
                            position: relative;
                        }
                        .sticky-column::before {
                            content: '';
                            position: absolute;
                            left: 0;
                            top: 0;
                            bottom: 0;
                            width: 1px;
                            background-color: #dee2e6;
                            z-index: 12;
                        }
                        thead .sticky-column::before {
                            z-index: 13;
                        }
                    `}
                </style>
                <div className="mb-4">
                    <Table hover responsive className="align-middle text-center">
                        <thead>
                            <tr>
                                <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>Sr No.</th>
                                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Bag No.</th>
                                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Available Quantity</th>
                                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Issue Quantity</th>
                                <th className="sticky-column" style={{ ...tableStyles.headerAlt, ...tableStyles.actionHeader, minWidth: "100px" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Input Row */}
                            <tr>
                                <td style={tableStyles.cell}></td>
                                <td style={tableStyles.cell}>
                                    <Autocomplete
                                        size="small"
                                        options={bagOptions || []}
                                        autoHighlight
                                        loading={loading}
                                        disabled={loading || editingBagId !== null}
                                        value={editingBagId !== null ? null : formik.values.bag_no}
                                        onChange={(_, val) => {
                                            formik.setFieldValue("bag_no", val);
                                            if (val) {
                                                const availableQty = parseFloat(String(val?.available_quantity)) || 0;
                                                formik.setFieldValue("available_quantity", availableQty);
                                                
                                                // Calculate remaining quantity needed
                                                const currentTotal = rows.reduce((sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0), 0);
                                                const remainingQty = (parseFloat(String(quantity)) || 0) - currentTotal;
                                                
                                                // Auto-fill issue quantity: min of remaining quantity and available quantity
                                                const autoFillQty = Math.min(remainingQty > 0 ? remainingQty : 0, availableQty);
                                                formik.setFieldValue("issue_quantity", autoFillQty > 0 ? autoFillQty : "");
                                            } else {
                                                formik.setFieldValue("available_quantity", "");
                                                formik.setFieldValue("issue_quantity", "");
                                            }
                                            // Cancel edit mode when bag selection changes
                                            if (editingBagId !== null) {
                                                setEditingBagId(null);
                                            }
                                        }}
                                        onBlur={() => formik.setFieldTouched("bag_no", true)}
                                        getOptionLabel={(option: BagOption) =>
                                            option ? `${option.bag_no} (Available:${option.available_quantity})` : ""
                                        }
                                        renderOption={(props, option: BagOption) =>
                                            autocompleteDropdownStyles.renderOption(
                                                props,
                                                option,
                                                (opt) => `${opt?.bag_no || ""} (Available:${opt?.available_quantity || 0})`
                                            )
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Select Bag No"
                                                error={formik.touched.bag_no && Boolean(formik.errors.bag_no)}
                                                helperText={
                                                    formik.touched.bag_no &&
                                                        typeof formik.errors.bag_no === "string"
                                                        ? formik.errors.bag_no
                                                        : undefined
                                                }
                                                sx={{
                                                    ...autocompleteDropdownStyles.inputSx,
                                                    '& .MuiInputBase-root': {
                                                        height: '32px',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        padding: '6px 12px',
                                                        height: '20px',
                                                    },
                                                }}
                                            />
                                        )}
                                        ListboxProps={{
                                            ...autocompleteDropdownStyles.ListboxProps,
                                            style: {
                                                maxHeight: "200px",
                                                overflow: "auto",
                                                zIndex: 1400,
                                            },
                                        }}
                                        componentsProps={{
                                            popper: {
                                                style: {
                                                    zIndex: 1400,
                                                },
                                                modifiers: [
                                                    {
                                                        name: 'preventOverflow',
                                                        enabled: true,
                                                    },
                                                ],
                                            },
                                        }}
                                        disablePortal={false}
                                    />
                                </td>
                                <td style={tableStyles.cell}>
                                    <TextField
                                        size="small"
                                        value={editingBagId !== null ? "" : (formik.values.available_quantity || "")}
                                        disabled
                                        placeholder="Auto-filled from bag selection"
                                        sx={{
                                            ...autocompleteDropdownStyles.inputSx,
                                            '& .MuiInputBase-root': {
                                                height: '32px',
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '6px 12px',
                                                height: '20px',
                                            },
                                        }}
                                    />
                                </td>
                                <td style={tableStyles.cell}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="Enter Issue Quantity"
                                        value={editingBagId !== null ? "" : formik.values.issue_quantity}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            formik.setFieldValue("issue_quantity", value);
                                        }}
                                        onBlur={formik.handleBlur}
                                        disabled={editingBagId !== null}
                                        error={!!formik.errors.issue_quantity && formik.touched.issue_quantity}
                                        helperText={
                                            formik.touched.issue_quantity && typeof formik.errors.issue_quantity === "string"
                                                ? formik.errors.issue_quantity
                                                : undefined
                                        }
                                        sx={{
                                            ...autocompleteDropdownStyles.inputSx,
                                            '& .MuiInputBase-root': {
                                                height: '32px',
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '6px 12px',
                                                height: '20px',
                                            },
                                        }}
                                    />
                                </td>
                                <td className="sticky-column" style={tableStyles.actionCell}>
                                    <div style={tableStyles.actionButtonContainer}>
                                        <SubmitButton
                                            isSubmitting={false}
                                            onClick={() => formik.handleSubmit()}
                                            disabled={editingBagId !== null}
                                        />
                                    </div>
                                </td>
                            </tr>

                            {/* Existing Rows */}
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={tableStyles.cell} className="text-center py-2 text-muted">
                                        No bags added. Please add at least one bag.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row, idx) => {
                                    const isEditing = editingBagId === row.id;
                                    const displayData = isEditing ? formik.values : row;

                                    return (
                                        <tr
                                            key={row.id}
                                            style={isEditing ? tableStyles.rowEditing : {}}
                                        >
                                            <td style={tableStyles.cell}>{idx + 1}</td>

                                            {/* Bag No */}
                                            <td style={tableStyles.cell}>
                                                {isEditing ? (
                                                    <Autocomplete
                                                        size="small"
                                                        options={bagOptions || []}
                                                        getOptionLabel={(opt: BagOption) => opt ? `${opt.bag_no} (Available:${opt.available_quantity})` : ""}
                                                        value={
                                                            (displayData.bag_no && typeof displayData.bag_no === 'object' && 'bag_no' in displayData.bag_no)
                                                                ? bagOptions.find(
                                                                    (b) => {
                                                                        const bagNo = displayData.bag_no as BagOption;
                                                                        return b.bag_no === bagNo.bag_no && b.inventory_item_id === bagNo.inventory_item_id;
                                                                    }
                                                                ) || null
                                                                : null
                                                        }
                                                        onChange={(_, value) => {
                                                            if (value) {
                                                                const availableQty = parseFloat(String(value.available_quantity)) || 0;
                                                                formik.setFieldValue("bag_no", value);
                                                                formik.setFieldValue("available_quantity", availableQty);
                                                                
                                                                // Calculate remaining quantity needed (excluding the row being edited)
                                                                const rowsExcludingEdited = rows.filter(row => row.id !== editingBagId);
                                                                const currentTotal = rowsExcludingEdited.reduce((sum, row) => sum + (parseFloat(String(row.issue_quantity)) || 0), 0);
                                                                const remainingQty = (parseFloat(String(quantity)) || 0) - currentTotal;
                                                                
                                                                // Auto-fill issue quantity: min of remaining quantity and available quantity
                                                                const autoFillQty = Math.min(remainingQty > 0 ? remainingQty : 0, availableQty);
                                                                formik.setFieldValue("issue_quantity", autoFillQty > 0 ? autoFillQty : "");
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                sx={{
                                                                    ...autocompleteDropdownStyles.inputSx,
                                                                    '& .MuiInputBase-root': {
                                                                        height: '32px',
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        padding: '6px 12px',
                                                                        height: '20px',
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        renderOption={(props, option) =>
                                                            autocompleteDropdownStyles.renderOption(
                                                                props,
                                                                option,
                                                                (opt) => `${opt?.bag_no || ""} (Available:${opt?.available_quantity || 0})`
                                                            )
                                                        }
                                                        ListboxProps={{
                                                            ...autocompleteDropdownStyles.ListboxProps,
                                                            style: {
                                                                maxHeight: "200px",
                                                                overflow: "auto",
                                                                zIndex: 1400,
                                                            },
                                                        }}
                                                        componentsProps={{
                                                            popper: {
                                                                style: {
                                                                    zIndex: 1400,
                                                                },
                                                                modifiers: [
                                                                    {
                                                                        name: 'preventOverflow',
                                                                        enabled: true,
                                                                    },
                                                                ],
                                                            },
                                                        }}
                                                        disablePortal={false}
                                                    />
                                                ) : (
                                                    <span>{row.bag_no || "-"}</span>
                                                )}
                                            </td>

                                            {/* Available Quantity */}
                                            <td style={tableStyles.cell}>
                                                {isEditing ? (
                                                    <TextField
                                                        size="small"
                                                        value={displayData.available_quantity || ""}
                                                        disabled
                                                        fullWidth
                                                        sx={{
                                                            ...autocompleteDropdownStyles.inputSx,
                                                            '& .MuiInputBase-root': {
                                                                height: '32px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                padding: '6px 12px',
                                                                height: '20px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    // <span>{row.available_quantity || "-"}</span>
                                                    <span>
                                                        {
                                                            (() => {
                                                                const bagOption = bagOptions.find(
                                                                    (option) => option.bag_no === row.bag_no && option.inventory_item_id === row.inventory_item_id
                                                                );
                                                                return bagOption
                                                                    ? Number(bagOption.available_quantity)
                                                                    : row?.available_quantity || "-";
                                                            })()
                                                        }
                                                    </span>

                                                )}
                                            </td>

                                            {/* Issue Quantity */}
                                            <td style={tableStyles.cell}>
                                                {isEditing ? (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={displayData.issue_quantity || ""}
                                                        onChange={(e) =>
                                                            formik.setFieldValue("issue_quantity", e.target.value)
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            ...autocompleteDropdownStyles.inputSx,
                                                            '& .MuiInputBase-root': {
                                                                height: '32px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                padding: '6px 12px',
                                                                height: '20px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <span>{row.issue_quantity || "-"}</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td
                                                style={isEditing ? tableStyles.actionCellEditing : tableStyles.actionCell}
                                                className="sticky-column"
                                            >
                                                {isEditing ? (
                                                    <div style={tableStyles.actionButtonContainer}>
                                                        <IconButton
                                                            onClick={() => formik.handleSubmit()}
                                                            size="small"
                                                            sx={{
                                                                color: tableStyles.primaryColor,
                                                                '&:hover': {
                                                                    backgroundColor: `${tableStyles.primaryColor}15`,
                                                                },
                                                            }}
                                                            title="Save"
                                                        >
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={handleCancelEdit}
                                                            size="small"
                                                            sx={{
                                                                color: '#6c757d',
                                                                '&:hover': {
                                                                    backgroundColor: '#f8f9fa',
                                                                },
                                                            }}
                                                            title="Cancel"
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </div>
                                                ) : (
                                                    <div style={tableStyles.actionButtonContainer}>
                                                        <EditButton
                                                            onClick={() => handleEdit(row)}
                                                            iconClassName="ph-pencil"
                                                        />
                                                        <DeleteButton
                                                            onClick={() => handleDelete(row.id)}
                                                            iconClassName="ph-trash"
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </div>

                <div className="d-flex justify-content-center gap-3 py-3">
                    <Button
                        variant="light"
                        onClick={handleModalClose}
                        style={{
                            backgroundColor: "white",
                            border: "1px solid #ced4da",
                            color: "#212529",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        style={{
                            background: Colors?.themeBgColor,
                            borderColor: Colors?.themeBgColor,
                            color: "#FFF",
                            minWidth: "70px",
                        }}
                    >
                        Save
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default BagSelectionModal;

